/**
 * THE SHORTCUT LIST, on `?`.
 *
 * A screen with single-letter shortcuts and no way to discover them has, for
 * most people, no shortcuts. `?` is the convention every product that has this
 * uses, and it is the one key a reader tries when they suspect there are some.
 *
 * IT IS A REAL DIALOG. `role="dialog" aria-modal="true"` — which is also what
 * `use-keyboard-shortcuts` looks for when it decides to stand down, so opening
 * this cannot leave `e` exporting behind it. Escape, the focus trap, the
 * restore-on-close and the body scroll lock all come from the shared dialog
 * stack (useDialogFocus), the same contract every other modal in the product
 * already uses — it used to hand-roll Escape and the first focus and skip the
 * rest.
 *
 * PORTALLED, like every other layer (see ../../../../modal/modal-portal.tsx):
 * it used to render inline under the screen's entrance transform, which is a
 * containing block for a fixed scrim — the sticky header stayed bright above
 * the dim.
 *
 * `open` is optional. The screen mounts this on `helpOpen` today, which plays
 * the entrance; passing `open={helpOpen}` instead keeps it mounted and plays
 * the exit too (the conditional is inside the Presence below).
 */
import React from 'react';
import { SHORTCUT_HELP } from './use-keyboard-shortcuts';
import { palette, radius, shadow, spacing, zIndex } from '../../../../../style-utils/theme';
import { down } from '../../../../../style-utils/media-queries';
import { useDialogFocus } from '../../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../../modal/modal-portal';
import { Presence, m, scrimMotion, useOverlayMotion } from '../../../../../motion';
import { HelpKey, HelpRow, HelpTitle, Button } from '../dashboard-screen.styled';

/* UNLIKE THE FILTER SHEET'S SCRIM, this one is shown at every width: the
   shortcuts exist at every width, and a reader on a tablet with a keyboard is
   exactly the person who goes looking for them. The card is centred by the
   scrim's flex box rather than by a translate of its own, because the entrance
   drives `transform` and a CSS centring transform would be overwritten by it. */
const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

const Card = m.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: min(420px, 100%);
  max-height: min(80vh, 80dvh);
  overflow: auto;
  padding: ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.lg};

  ${down('sm')} {
    width: 100%;
    border-radius: ${radius.lg} ${radius.lg} 0 0;
  }
`;

interface ShortcutHelpProps {
  open?: boolean;
  onClose: () => void;
}

export const ShortcutHelp: React.FC<ShortcutHelpProps> = ({
  open,
  onClose,
}) => {
  const isOpen = open ?? true;
  const dialogRef = useDialogFocus(isOpen, onClose);
  const cardMotion = useOverlayMotion('modal');

  return (
    <Presence>
      {isOpen ? (
        <ModalPortal key="shortcut-help">
          <Scrim {...scrimMotion} onClick={onClose}>
            <Card
              {...cardMotion}
              ref={dialogRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label="Keyboard shortcuts"
              data-testid="shortcut-help"
              onClick={event => event.stopPropagation()}
            >
              <HelpTitle>Keyboard shortcuts</HelpTitle>
              {SHORTCUT_HELP.map(row => (
                <HelpRow key={row.keys}>
                  <HelpKey>{row.keys}</HelpKey>
                  <span>{row.does}</span>
                </HelpRow>
              ))}
              <HelpRow>
                <HelpKey>?</HelpKey>
                <span>This list</span>
              </HelpRow>
              {/* data-autofocus: the shared hook lands focus here, as the old
                  inline effect did — Close is the one control in the list. */}
              <Button
                type="button"
                onClick={onClose}
                data-testid="help-close"
                data-autofocus
              >
                Close
              </Button>
            </Card>
          </Scrim>
        </ModalPortal>
      ) : null}
    </Presence>
  );
};
